import { AIR, type BlockDef, type BlockId, type BlockRegistry } from "./types";

export const AIR_BLOCK: BlockDef = Object.freeze({
  id: AIR,
  name: "air",
  solid: false,
  opaque: false,
  hardness: 0,
  toolTier: 0,
  emission: 0,
});

/** Immutable registry with stable id and name lookup. Unknown ids resolve to air. */
export class DefaultBlockRegistry implements BlockRegistry {
  readonly all: readonly BlockDef[];
  private readonly ids = new Map<BlockId, BlockDef>();
  private readonly names = new Map<string, BlockDef>();

  constructor(definitions: readonly BlockDef[]) {
    const defs = definitions.some((definition) => definition.id === AIR)
      ? definitions
      : [AIR_BLOCK, ...definitions];
    for (const definition of defs) {
      if (!Number.isInteger(definition.id) || definition.id < 0 || definition.id > 0xffff) {
        throw new RangeError(`invalid block id ${definition.id}`);
      }
      if (this.ids.has(definition.id)) throw new Error(`duplicate block id ${definition.id}`);
      if (this.names.has(definition.name)) throw new Error(`duplicate block name ${definition.name}`);
      const frozen = Object.freeze({ ...definition });
      this.ids.set(frozen.id, frozen);
      this.names.set(frozen.name, frozen);
    }
    const air = this.ids.get(AIR);
    if (!air || air.solid || air.opaque) throw new Error("block 0 must be non-solid, non-opaque air");
    this.all = Object.freeze([...this.ids.values()].sort((a, b) => a.id - b.id));
  }

  get(id: BlockId): BlockDef {
    return this.ids.get(id) ?? this.ids.get(AIR) ?? AIR_BLOCK;
  }

  byName(name: string): BlockDef | undefined {
    return this.names.get(name);
  }
}

export const DEFAULT_BLOCKS = new DefaultBlockRegistry([
  AIR_BLOCK,
  { id: 1, name: "stone", solid: true, opaque: true, hardness: 1.5, toolTier: 1, emission: 0 },
  { id: 2, name: "dirt", solid: true, opaque: true, hardness: 0.5, toolTier: 0, emission: 0 },
  { id: 3, name: "grass", solid: true, opaque: true, hardness: 0.6, toolTier: 0, emission: 0 },
  { id: 4, name: "glass", solid: true, opaque: false, hardness: 0.3, toolTier: 0, emission: 0 },
  { id: 5, name: "water", solid: false, opaque: false, hardness: Number.POSITIVE_INFINITY, toolTier: 0, emission: 0 },
]);
